import React, { useState, useContext } from "react"
import { useParams } from "react-router-dom"
import { v4 as uuidv4 } from "uuid"

import { useQuery, useMutation, useLazyQuery } from "@apollo/client"

//gsql queries
import { GET_USER, EDIT_USER, GET_USERS } from "../queries"

// Context
import { UserContext } from "../../../../context/userContext"

//RefEntity user form
import ReferralEntityUserForm from "./Form/ReferralEntityUserForm"

//Referral API
import { referralsApiUserGet, referralsApiUserEditPut } from "../../partnerAPI"
import { LogError } from "../../../../helpers/logger"

export default function ReferralEntityUserEdit({
  onCancel,
  onSubmit,
  partnerOrganizationId,
  userId,
}) {
  const { id } = useParams()
  const userCtx = useContext(UserContext)

  const organizationId = !!partnerOrganizationId
    ? partnerOrganizationId
    : userCtx.session.user.organization_id

  const [isLoading, setIsLoading] = useState(true)
  const [formId, setFormId] = useState(() => uuidv4())

  const [userProfile, setUserProfile] = useState({
    admin_staff_id: userCtx.session.user.staff_id,
    admin_staff_alias: userCtx.session.user.staff_alias,
    admin_staff_enterprise_id: userCtx.session.user.enterprise_id,
    admin_staff_organization_id: userCtx.session.user.organization_id,
    admin_staff_enterprise_alias: userCtx.session.user.enterprise_alias,
    admin_staff_organization_alias: userCtx.session.user.organization_alias,
    staff_id: undefined,
    staff_id_stamp: undefined,
    staff_name_first: undefined,
    staff_name_last: undefined,
    staff_email_address: undefined,
    staff_organization_id: undefined,
    staff_role: undefined,
  })
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  useQuery(GET_USER, {
    variables: { uid: userId || id, organization_id: organizationId },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      let profile = {
        ...userProfile,
        staff_organization_id: data.user.organization_id,
        staff_id: data.user.staff_id,
        staff_id_stamp: data.user.staff_id_stamp || "",
        staff_name_first: data.user.staff_name_first,
        staff_name_last: data.user.staff_name_last,
        staff_email_address: data.user.staff_email_address,
        staff_role: data.user.staff_role,
      }

      setUserProfile(profile)

      referralsApiUserGet(
        userCtx.tokens.getMainToken(),
        data.user.organization_id,
        data.user.staff_id,
      )
        .then((result) => {
          //Convert service selections for tree select
          let serviceTypeSelection = {}

          result.practitionerReferralCategories?.forEach((service) => {
            let newService = {}
            newService[service.id] = {
              checked: true,
              partialChecked: false,
            }

            //Inject the object into serviceTypeSelection
            Object.assign(serviceTypeSelection, newService)
          })

          let roleType =
            !!result.practitionerRoleTypes &&
            result.practitionerRoleTypes.length > 0
              ? result.practitionerRoleTypes[0].id
              : undefined

          setUserProfile({
            ...profile,
            referralEntityUserId: result.id,
            isPractitioner: result.isPractitioner,
            practitionerNpi: result.practitionerNpi,
            practitionerLocations: result.practitionerLocations,
            practitionerRoleType: roleType,
            practitionerReferralCategories: serviceTypeSelection || [],
          })
          setIsInitialLoad(false)
          setIsLoading(false)
        })
        .catch((error) => {
          let message =
            "There was an unexpected error while retrieving the user list, please contact the administrator"

          if (!!error.isAxiosError) {
            message = `There was an unexpected error while retrieving the user profile: ${error.response.data}`
          }

          userCtx.setNotificationError(message)
          LogError(message, error)
          setIsLoading(false)
          onCancel()
        })
    },
    onError: (error) => {
      userCtx.setNotificationError(
        "There was an unexpected error while retrieving the user, please contact the administrator",
      )
      LogError("There was an unexpected error while retrieving the user", error)
      setIsLoading(false)
      onCancel()
    },
  })

  const [getUsers] = useLazyQuery(GET_USERS, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
  })

  const [editUser] = useMutation(EDIT_USER, {
    variables: {
      uid: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
  })

  async function updateUser(submitProfile) {
    try {
      if (submitProfile !== null) {
        setIsLoading(true)

        //if the user is making itself inactive, make sure that there are other active admins available still
        if (
          submitProfile.variables.staff_id === userCtx.session.user.staff_id &&
          submitProfile.variables.staff_role !== 9 &&
          userCtx.session.user.staff_role === 9
        ) {
          const variables = {
            variables: {
              uid: userCtx.session.user.staff_id,
              organization_id: userCtx.session.user.organization_id,
            },
          }
          const users = await getUsers(variables) //Parameters set at declaration
          const admins = users.data?.users.filter(
            (user) =>
              user.staff_role === 9 &&
              user.staff_id !== userCtx.session.user.staff_id,
          )

          if (!admins || admins?.length === 0) {
            userCtx.setNotificationError(
              "Aborting user update, at least one active administrator is required per organization",
            )
            setIsLoading(false)
            return
          }
        }

        const soarSubmitVariable = {
          variables: {
            form_id: formId,
            admin_staff_id: submitProfile.variables.admin_staff_id,
            admin_staff_alias: submitProfile.variables.admin_staff_alias,
            admin_staff_enterprise_id:
              submitProfile.variables.admin_staff_enterprise_id,
            admin_staff_organization_id:
              submitProfile.variables.admin_staff_organization_id,
            admin_staff_enterprise_alias:
              submitProfile.variables.admin_staff_enterprise_alias,
            admin_staff_organization_alias:
              submitProfile.variables.admin_staff_organization_alias,
            staff_email_address: submitProfile.variables.staff_email_address,
            staff_id: submitProfile.variables.staff_id,
            staff_id_stamp: submitProfile.variables.staff_id_stamp,
            staff_name_first: submitProfile.variables.staff_name_first,
            staff_name_last: submitProfile.variables.staff_name_last,
            staff_organization_id:
              submitProfile.variables.staff_organization_id,
            staff_role: submitProfile.variables.staff_role,
            staff_primary_supervisor: "",
            staff_hire_date: "",
          },
        }

        const result = await editUser(soarSubmitVariable)

        if (!!result.data?.editUser?.staff_id) {
          //Fix services
          const selectedServices =
            submitProfile.variables.practitionerReferralCategories
          let serviceSelection = []
          Object.keys(selectedServices).forEach((key) => {
            if (selectedServices[key].checked) {
              serviceSelection = [...serviceSelection, { Id: key, Name: "_" }]
            }
          })

          referralsApiUserEditPut(userCtx.tokens.getMainToken(), {
            processingUserId: userCtx.session.user.staff_id,
            processingOrganizationId: userCtx.session.user.organization_id,
            id: submitProfile.variables.referralEntityUserId,
            uid: submitProfile.variables.staff_id,
            email: submitProfile.variables.staff_email_address,
            firstName: submitProfile.variables.staff_name_first,
            lastName: submitProfile.variables.staff_name_last,
            isPractitioner: submitProfile.variables.isPractitioner,
            practitionerNpi: submitProfile.variables.practitionerNpi,
            practitionerLocations:
              submitProfile.variables.practitionerLocations.map((x) => {
                return {
                  name: x.name,
                  referralLocationId: x.referralLocationId,
                }
              }),
            practitionerRoleTypes: [
              { id: submitProfile.variables.practitionerRoleType },
            ],
            practitionerReferralCategories: serviceSelection,
          })
            .then((result) => {
              setFormId(uuidv4())
              onSubmit(result)
            })
            .catch((ex) => {
              userCtx.setNotificationError(
                "There was an unexpected error while updating the user, please see the console for details",
              )
              LogError("Unable to update user information (referrals)", ex)
              setIsLoading(false)
            })
        } else {
          userCtx.setNotificationError(
            "There was an unexpected error while updating the user, contact an administrator",
          )
          LogError(
            "Unable to update user information (main-success-no-user-id)",
            result,
          )
          setIsLoading(false)
        }
      }
    } catch (error) {
      userCtx.setNotificationError(
        "Unable to update user information (main), please see the console for details",
      )
      LogError("Unable to update user information (main)", error)
      setIsLoading(false)
    }
  }

  return (
    <div>
      <ReferralEntityUserForm
        isInitialLoad={isInitialLoad}
        isLoading={isLoading}
        userProfile={userProfile}
        handleFormSubmit={updateUser}
        requireUserPassword={false}
        handleCancel={onCancel}
        partnerOrganizationId={organizationId}
      />
    </div>
  )
}
